import { useEffect, useRef, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ConfirmModal from './ConfirmModal'

interface InactivityDetectorProps {
  warningTime?: number // Time in milliseconds before showing warning (default: 5 minutes)
  logoutTime?: number // Time in milliseconds before auto-logout (default: 30 minutes)
  enabled?: boolean // Whether inactivity detection is enabled (default: true)
  onLogout?: () => void // Optional logout callback
}

/**
 * InactivityDetector - Detects user inactivity and shows warning before auto-logout
 */
export default function InactivityDetector({
  warningTime = 5 * 60 * 1000, // 5 minutes
  logoutTime = 30 * 60 * 1000, // 30 minutes
  enabled = true,
  onLogout
}: InactivityDetectorProps) {
  const navigate = useNavigate()
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastActivityRef = useRef(Date.now())
  const warningShownRef = useRef(false)
  const wasEnabledRef = useRef(false)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)

  const handleLogout = useCallback(() => {
    if (onLogout) {
      onLogout()
    } else {
      // Dispatch logout event to trigger AuthContext logout
      window.dispatchEvent(new CustomEvent('force-logout'))
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_refresh_token')
      localStorage.removeItem('admin_user')
      navigate('/admin/login')
      window.location.reload()
    }
  }, [navigate, onLogout])

  const showWarning = useCallback(() => {
    if (warningShownRef.current) return
    warningShownRef.current = true

    const timeUntilLogout = logoutTime - warningTime
    const minutes = Math.floor(timeUntilLogout / 60000)
    const seconds = Math.floor((timeUntilLogout % 60000) / 1000)
    
    setTimeRemaining(minutes * 60 + seconds)
    setShowWarningModal(true)

    // Start countdown timer
    const countdownInterval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval)
          handleLogout()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    // Store interval ref for cleanup
    warningTimerRef.current = countdownInterval as any
  }, [warningTime, logoutTime, handleLogout])

  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current)
      logoutTimerRef.current = null
    }

    if (!enabled) return

    const now = Date.now()
    const timeSinceActivity = now - lastActivityRef.current

    // If user has been inactive for longer than warning time, show warning immediately
    if (timeSinceActivity >= warningTime) {
      showWarning()
    } else {
      // Set timer for warning
      const timeUntilWarning = warningTime - timeSinceActivity
      warningTimerRef.current = setTimeout(() => {
        showWarning()
        // Set timer for logout after warning
        logoutTimerRef.current = setTimeout(() => {
          handleLogout()
        }, logoutTime - warningTime)
      }, timeUntilWarning)
    }
  }, [enabled, warningTime, logoutTime, showWarning, handleLogout])

  const handleStayLoggedIn = useCallback(() => {
    setShowWarningModal(false)
    lastActivityRef.current = Date.now()
    warningShownRef.current = false
    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current as any)
      warningTimerRef.current = null
    }
    resetTimers()
  }, [resetTimers])

  const handleLogoutNow = useCallback(() => {
    setShowWarningModal(false)
    handleLogout()
  }, [handleLogout])

  const handleActivity = useCallback(() => {
    if (!enabled) return
    
    lastActivityRef.current = Date.now()
    warningShownRef.current = false
    resetTimers()
  }, [enabled, resetTimers])

  useEffect(() => {
    if (!enabled) {
      wasEnabledRef.current = false
      return
    }

    // When we first enable (e.g. user just logged in), start the inactivity window from now.
    // Otherwise we'd use lastActivityRef from when the component mounted on the login page.
    if (!wasEnabledRef.current) {
      lastActivityRef.current = Date.now()
      warningShownRef.current = false
      wasEnabledRef.current = true
    }

    // List of events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ]

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true)
    })

    // Initialize timers (inactivity period starts from lastActivityRef, now correct)
    resetTimers()

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true)
      })
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current)
      }
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current)
      }
    }
  }, [enabled, handleActivity, resetTimers])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <>
      <ConfirmModal
        isOpen={showWarningModal}
        title="Session Timeout Warning"
        message={`You've been inactive for a while. You will be logged out in ${formatTime(timeRemaining)}.\n\nWould you like to stay logged in?`}
        confirmText="Stay Logged In"
        cancelText="Log Out Now"
        variant="warning"
        onConfirm={handleStayLoggedIn}
        onCancel={handleLogoutNow}
      />
    </>
  )
}
